import { useCallback, useRef } from 'react';

import UploadAbortedError from './UploadAbortedError';

// When a length of the file can be computed, we use it to compute the progress but cap it at 95%.
// This is because File could be 100% uploaded but the server still didn't return a response.
// If the length is not computable, we just set the progress to 50% so the user can see something is happening.
const computeProgress = ({ lengthComputable, loaded, total }: ProgressEvent) =>
    lengthComputable ? Math.min(Math.round((loaded / total) * 100), 95) : 50;

const sendUploadRequest = (
    uploadUrl: string,
    file: File,
    onProgress: (progress: number) => void,
    onSuccess: (result: { styleGuideId: string }) => void,
    onError: (error: Error) => void,
) => {
    const formData = new FormData();
    formData.append('file', file);

    const request = new XMLHttpRequest();

    request.upload.addEventListener('progress', (event) => {
        onProgress(computeProgress(event));
    });

    request.open('POST', uploadUrl);

    request.onreadystatechange = () => {
        if (request.readyState !== XMLHttpRequest.DONE) {
            return;
        }

        if (request.status === 0) {
            onError(new UploadAbortedError());

            // This can happen if the user cancels the upload
            return;
        }

        if (request.status !== 200) {
            onError(new Error('Upload failed'));

            return;
        }

        const response: { id: string } = JSON.parse(request.response);

        // Complete the progress bar once the server return a response
        onProgress(100);
        onSuccess({ styleGuideId: response.id });
    };

    request.send(formData);

    return () => request.abort();
};

const useFileUpload = (teamId: number) => {
    const uploadCache = useRef<Map<File, () => void>>(new Map());

    const uploadFile = useCallback(
        (file: File, onProgress: (progress: number) => void) =>
            new Promise<{ styleGuideId: string }>((resolve, reject) => {
                const uploadUrl = `/style-guide/${teamId}/upload`;
                const upload = sendUploadRequest(
                    uploadUrl,
                    file,
                    onProgress,
                    (result) => {
                        uploadCache.current.delete(file);
                        resolve(result);
                    },
                    (error) => {
                        uploadCache.current.delete(file);
                        reject(error);
                    },
                );
                uploadCache.current.set(file, upload);
            }),
        [teamId],
    );

    const cancelUpload = useCallback((file: File) => {
        uploadCache.current.get(file)?.();
        uploadCache.current.delete(file);
    }, []);

    return { uploadFile, cancelUpload };
};

export default useFileUpload;
